/**
 * UserDashboard.tsx — The Locker Room
 *
 * Sprint 2A: User Dashboard
 * Three-tab authenticated dashboard: My Reviews | Verification | Profile
 */

import React, { useEffect, useState, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  User,
  Shield,
  FileText,
  Edit2,
  Upload,
  CheckCircle,
  XCircle,
  Clock,
  Star,
  ChevronRight,
  LogOut,
  Award,
  AlertCircle,
} from 'lucide-react'

import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SchoolInfo {
  institution_name: string
  slug: string
}

interface Review {
  id: string
  school_id: number
  user_id: string
  sport: string
  gender: string
  facilities_rating: number
  coaching_rating: number
  balance_rating: number
  support_rating: number
  culture_rating: number
  equity_rating: number
  review_text: string | null
  pros: string | null
  cons: string | null
  helpful_count: number
  flagged_count: number
  is_anonymous: boolean
  moderation_status: 'pending' | 'approved' | 'rejected'
  created_at: string
  schools?: SchoolInfo | null
}

interface RosterSubmission {
  id: string
  user_id: string
  school_id: number | null
  sport: string | null
  gender: string | null
  graduation_year: number | null
  roster_url: string | null
  notes: string | null
  status: 'pending' | 'approved' | 'rejected'
  created_at: string
}

type Tab = 'reviews' | 'verification' | 'profile'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function avgRating(review: Review): number {
  const vals = [
    review.facilities_rating,
    review.coaching_rating,
    review.balance_rating,
    review.support_rating,
    review.culture_rating,
    review.equity_rating,
  ].filter((v) => typeof v === 'number')
  if (vals.length === 0) return 0
  return vals.reduce((a, b) => a + b, 0) / vals.length
}

function truncate(text: string | null | undefined, max: number): string {
  if (!text) return ''
  return text.length > max ? text.slice(0, max).trimEnd() + '…' : text
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

// Skeleton loader block
function SkeletonBlock({ className }: { className?: string }) {
  return (
    <div
      className={`animate-pulse bg-[#1E1F2E] rounded-lg ${className ?? ''}`}
    />
  )
}

// Star rating display
function StarRating({ value }: { value: number }) {
  const rounded = Math.round(value * 2) / 2
  return (
    <div className="flex items-center gap-0.5" aria-label={`${rounded.toFixed(1)} out of 5 stars`}>
      {[1, 2, 3, 4, 5].map((i) => {
        const filled = i <= rounded
        return (
          <Star
            key={i}
            size={14}
            className={filled ? 'text-[#14B8A6] fill-[#14B8A6]' : 'text-[#555570]'}
          />
        )
      })}
      <span className="ml-1.5 text-xs text-[#8888a8]">{rounded.toFixed(1)}</span>
    </div>
  )
}

// Moderation status pill
function ModerationBadge({ status }: { status: Review['moderation_status'] }) {
  const map = {
    pending: 'bg-[rgba(20,184,166,0.20)] text-[#14B8A6] border-[rgba(20,184,166,0.30)]',
    approved: 'bg-green-500/20 text-green-400 border-green-500/30',
    rejected: 'bg-red-500/20 text-red-400 border-red-500/30',
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${map[status]}`}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

// Review card
function ReviewSummaryCard({ review }: { review: Review }) {
  const avg = avgRating(review)
  const schoolName =
    review.schools?.institution_name ?? `School #${review.school_id}`
  const slug = review.schools?.slug ?? ''

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#14151F] border border-[#2a2a3c] rounded-xl p-5 hover:border-[#2a2a3c] transition-colors"
    >
      {/* Header row */}
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <p className="text-white font-semibold text-sm">{schoolName}</p>
          <p className="text-[#8888a8] text-xs mt-0.5">
            {review.sport} · {review.gender} · {formatDate(review.created_at)}
          </p>
        </div>
        <ModerationBadge status={review.moderation_status} />
      </div>

      {/* Stars */}
      <div className="mb-3">
        <StarRating value={avg} />
      </div>

      {/* Review text snippet */}
      {review.review_text && (
        <p className="text-[#a8a8c0] text-sm leading-relaxed mb-3">
          {truncate(review.review_text, 120)}
        </p>
      )}

      {/* Pros / Cons */}
      {(review.pros || review.cons) && (
        <div className="flex flex-col sm:flex-row gap-2 mb-3">
          {review.pros && (
            <div className="flex-1 bg-[#1E1F2E] rounded-lg px-3 py-2">
              <p className="text-green-400 text-xs font-semibold uppercase tracking-wide mb-1">
                Pros
              </p>
              <p className="text-[#a8a8c0] text-xs">{truncate(review.pros, 60)}</p>
            </div>
          )}
          {review.cons && (
            <div className="flex-1 bg-[#1E1F2E] rounded-lg px-3 py-2">
              <p className="text-red-400 text-xs font-semibold uppercase tracking-wide mb-1">
                Cons
              </p>
              <p className="text-[#a8a8c0] text-xs">{truncate(review.cons, 60)}</p>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between">
        <span className="text-[#555570] text-xs">
          {review.helpful_count} found helpful
        </span>
        {slug && (
          <Link
            to={`/school/${slug}`}
            className="flex items-center gap-1 text-[#14B8A6] hover:text-[#14B8A6] text-xs font-medium transition-colors"
          >
            View School <ChevronRight size={12} />
          </Link>
        )}
      </div>
    </motion.div>
  )
}

// ---------------------------------------------------------------------------
// Tab 1: My Reviews
// ---------------------------------------------------------------------------

function ReviewsTab({ userId }: { userId: string }) {
  const [reviews, setReviews] = useState<Review[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    async function fetchReviews() {
      setLoading(true)
      setError(null)

      // Try with join first
      const { data, error: fetchError } = await supabase
        .from('reviews')
        .select('*, schools(institution_name, slug)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })

      if (fetchError) {
        // Fall back to reviews only, then fetch school data separately
        const { data: reviewsOnly, error: fallbackError } = await supabase
          .from('reviews')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false })

        if (fallbackError) {
          setError(fallbackError.message)
          setLoading(false)
          return
        }

        const reviewList = (reviewsOnly ?? []) as Review[]

        // Fetch school names separately for unique school_ids
        const schoolIds = Array.from(new Set(reviewList.map((r) => r.school_id)))
        if (schoolIds.length > 0) {
          const { data: schools } = await supabase
            .from('schools')
            .select('school_id, institution_name, slug')
            .in('school_id', schoolIds)

          const schoolMap: Record<number, SchoolInfo> = {}
          if (schools) {
            for (const s of schools as Array<{ school_id: number; institution_name: string; slug: string }>) {
              schoolMap[s.school_id] = {
                institution_name: s.institution_name,
                slug: s.slug,
              }
            }
          }

          setReviews(
            reviewList.map((r) => ({
              ...r,
              schools: schoolMap[r.school_id] ?? null,
            }))
          )
        } else {
          setReviews(reviewList)
        }
      } else {
        setReviews((data ?? []) as Review[])
      }

      setLoading(false)
    }

    fetchReviews()
  }, [userId])

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-[#14151F] border border-[#2a2a3c] rounded-xl p-5 space-y-3">
            <SkeletonBlock className="h-4 w-48" />
            <SkeletonBlock className="h-3 w-32" />
            <SkeletonBlock className="h-16 w-full" />
          </div>
        ))}
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 flex items-start gap-3">
        <AlertCircle size={20} className="text-red-400 mt-0.5 shrink-0" />
        <div>
          <p className="text-red-400 font-semibold text-sm">Failed to load reviews</p>
          <p className="text-red-300/70 text-xs mt-1">{error}</p>
        </div>
      </div>
    )
  }

  if (reviews.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="w-16 h-16 bg-[#1E1F2E] rounded-full flex items-center justify-center mx-auto mb-4">
          <FileText size={28} className="text-[#555570]" />
        </div>
        <h3 className="text-white font-semibold text-lg mb-2">No reviews yet</h3>
        <p className="text-[#8888a8] text-sm mb-6 max-w-sm mx-auto">
          Share your experience as a collegiate athlete to help others make informed decisions.
        </p>
        <Link
          to="/directory"
          className="inline-flex items-center gap-2 bg-[#14B8A6] text-black font-bold px-5 py-2.5 rounded-lg hover:bg-[#14B8A6] transition-colors text-sm"
        >
          <Edit2 size={15} /> Write Your First Review
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {reviews.map((review) => (
        <ReviewSummaryCard key={review.id} review={review} />
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 2: Verification
// ---------------------------------------------------------------------------

interface VerificationTabProps {
  userId: string
  schoolId: string | null
  sport: string | null
  gender: string | null
  graduationYear: number | null
  verificationStatus: 'pending' | 'approved' | 'rejected'
}

function StepIcon({ state }: { state: 'complete' | 'pending' | 'rejected' | 'not_started' }) {
  if (state === 'complete')
    return <CheckCircle size={22} className="text-green-400" />
  if (state === 'pending')
    return <Clock size={22} className="text-[#14B8A6]" />
  if (state === 'rejected')
    return <XCircle size={22} className="text-red-400" />
  return (
    <div className="w-5.5 h-5.5 rounded-full border-2 border-[#3a3a52] bg-transparent" />
  )
}

function VerificationTab({
  userId,
  schoolId,
  sport,
  gender,
  graduationYear,
  verificationStatus,
}: VerificationTabProps) {
  const [submission, setSubmission] = useState<RosterSubmission | null>(null)
  const [submissionLoading, setSubmissionLoading] = useState(true)
  const [formData, setFormData] = useState({
    roster_url: '',
    notes: '',
    sport: sport ?? '',
    graduation_year: graduationYear ?? new Date().getFullYear(),
  })
  const [submitting, setSubmitting] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const fetchSubmission = useCallback(async () => {
    setSubmissionLoading(true)
    const { data } = await supabase
      .from('roster_submissions')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    setSubmission((data as RosterSubmission) ?? null)
    setSubmissionLoading(false)
  }, [userId])

  useEffect(() => {
    fetchSubmission()
  }, [fetchSubmission])

  async function handleSubmitRoster(e: React.FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    setSubmitError(null)

    const { error } = await supabase.from('roster_submissions').insert({
      user_id: userId,
      school_id: schoolId ? parseInt(schoolId, 10) : null,
      sport: formData.sport || null,
      gender: gender ?? null,
      graduation_year: formData.graduation_year || null,
      roster_url: formData.roster_url,
      notes: formData.notes || null,
      status: 'pending',
    })

    if (error) {
      setSubmitError(error.message)
    } else {
      setSubmitSuccess(true)
      await fetchSubmission()
    }
    setSubmitting(false)
  }

  // Determine step states
  const step1State: 'complete' | 'pending' | 'rejected' | 'not_started' = 'complete' // always complete if logged in
  let step2State: 'complete' | 'pending' | 'rejected' | 'not_started' = 'not_started'
  let step3State: 'complete' | 'pending' | 'rejected' | 'not_started' = 'not_started'

  if (submission) {
    step2State = 'complete'
    if (submission.status === 'approved' || verificationStatus === 'approved')
      step3State = 'complete'
    else if (submission.status === 'rejected' || verificationStatus === 'rejected')
      step3State = 'rejected'
    else step3State = 'pending'
  } else if (verificationStatus === 'approved') {
    step2State = 'complete'
    step3State = 'complete'
  }

  const showForm =
    verificationStatus !== 'approved' &&
    !submission &&
    !submitSuccess &&
    !submissionLoading

  const showResubmit =
    (verificationStatus === 'rejected' || submission?.status === 'rejected') &&
    !submitSuccess &&
    !submissionLoading

  return (
    <div className="space-y-6">
      {/* Approved banner */}
      {verificationStatus === 'approved' && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500/15 border border-green-500/30 rounded-xl p-5 flex items-center gap-4"
        >
          <Award size={32} className="text-green-400 shrink-0" />
          <div>
            <p className="text-green-300 font-bold text-lg">Verified Athlete ✓</p>
            <p className="text-green-400/70 text-sm mt-0.5">
              Your athlete status has been confirmed. You can submit reviews.
            </p>
          </div>
        </motion.div>
      )}

      {/* Step tracker */}
      <div className="bg-[#14151F] border border-[#2a2a3c] rounded-xl p-6">
        <h3 className="text-white font-semibold text-base mb-6">Verification Journey</h3>
        <div className="space-y-0">
          {[
            {
              step: 1,
              label: 'Email Verified',
              desc: 'You created an account and confirmed your email.',
              state: step1State,
            },
            {
              step: 2,
              label: 'Roster Evidence Submitted',
              desc: 'You uploaded a link or document proving your roster membership.',
              state: step2State,
            },
            {
              step: 3,
              label: 'Admin Review',
              desc:
                step3State === 'rejected'
                  ? 'Your submission was not approved. Please re-submit with additional evidence.'
                  : step3State === 'complete'
                  ? 'Your verification has been approved by our team.'
                  : 'Our team will review your submission within 1–3 business days.',
              state: step3State,
            },
          ].map((item, idx) => (
            <div key={item.step} className="flex gap-4">
              {/* Vertical line + icon */}
              <div className="flex flex-col items-center">
                <div className="mt-0.5">
                  <StepIcon state={item.state} />
                </div>
                {idx < 2 && (
                  <div className="w-px flex-1 bg-[#2a2a3c] my-2" />
                )}
              </div>
              {/* Content */}
              <div className={`pb-6 ${idx === 2 ? 'pb-0' : ''}`}>
                <p className="text-white font-medium text-sm">{item.label}</p>
                <p className="text-[#8888a8] text-xs mt-0.5 leading-relaxed">{item.desc}</p>
                {item.step === 2 && submission && (
                  <p className="text-[#555570] text-xs mt-1">
                    Submitted {formatDate(submission.created_at)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Loading state */}
      {submissionLoading && (
        <div className="space-y-2">
          <SkeletonBlock className="h-10 w-full" />
          <SkeletonBlock className="h-24 w-full" />
        </div>
      )}

      {/* Submit success */}
      {submitSuccess && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500/10 border border-green-500/30 rounded-xl p-5 flex items-start gap-3"
        >
          <CheckCircle size={20} className="text-green-400 mt-0.5 shrink-0" />
          <div>
            <p className="text-green-300 font-semibold text-sm">Submission received!</p>
            <p className="text-green-400/70 text-xs mt-1">
              Our team will review your roster evidence within 1–3 business days. You'll be
              notified once a decision is made.
            </p>
          </div>
        </motion.div>
      )}

      {/* Submission form — initial or re-submit */}
      {(showForm || showResubmit) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-[#14151F] border border-[#2a2a3c] rounded-xl p-6"
        >
          {showResubmit && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-5 flex items-start gap-3">
              <AlertCircle size={16} className="text-red-400 mt-0.5 shrink-0" />
              <div>
                <p className="text-red-400 font-semibold text-xs">Submission not approved</p>
                {submission?.notes && (
                  <p className="text-red-300/70 text-xs mt-1">{submission.notes}</p>
                )}
                <p className="text-red-300/70 text-xs mt-1">
                  Please provide additional evidence and re-submit.
                </p>
              </div>
            </div>
          )}

          <h3 className="text-white font-semibold text-base mb-5 flex items-center gap-2">
            <Upload size={18} className="text-[#14B8A6]" />
            Submit Roster Evidence
          </h3>

          <form onSubmit={handleSubmitRoster} className="space-y-4">
            <div>
              <label className="block text-[#a8a8c0] text-sm font-medium mb-1.5">
                Link to Roster Page or Document *
              </label>
              <input
                type="url"
                required
                value={formData.roster_url}
                onChange={(e) => setFormData((p) => ({ ...p, roster_url: e.target.value }))}
                placeholder="https://example.com/team-roster"
                className="w-full bg-[#1E1F2E] border border-[#2a2a3c] rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-[#555570] focus:outline-none focus:border-[#14B8A6] transition-colors"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[#a8a8c0] text-sm font-medium mb-1.5">
                  Sport
                </label>
                <input
                  type="text"
                  value={formData.sport}
                  onChange={(e) => setFormData((p) => ({ ...p, sport: e.target.value }))}
                  placeholder="e.g. Basketball"
                  className="w-full bg-[#1E1F2E] border border-[#2a2a3c] rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-[#555570] focus:outline-none focus:border-[#14B8A6] transition-colors"
                />
              </div>
              <div>
                <label className="block text-[#a8a8c0] text-sm font-medium mb-1.5">
                  Graduation Year
                </label>
                <input
                  type="number"
                  min={2000}
                  max={2035}
                  value={formData.graduation_year}
                  onChange={(e) =>
                    setFormData((p) => ({ ...p, graduation_year: parseInt(e.target.value, 10) }))
                  }
                  className="w-full bg-[#1E1F2E] border border-[#2a2a3c] rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-[#14B8A6] transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#a8a8c0] text-sm font-medium mb-1.5">
                Additional Notes
              </label>
              <textarea
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))}
                placeholder="Any context that helps verify your roster status…"
                className="w-full bg-[#1E1F2E] border border-[#2a2a3c] rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-[#555570] focus:outline-none focus:border-[#14B8A6] transition-colors resize-none"
              />
            </div>

            {submitError && (
              <div className="flex items-center gap-2 text-red-400 text-xs">
                <AlertCircle size={14} />
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#14B8A6] text-black font-bold py-2.5 rounded-lg hover:bg-[#14B8A6] transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Submitting…
                </>
              ) : (
                <>
                  <Upload size={15} />
                  Submit Roster Evidence
                </>
              )}
            </button>
          </form>
        </motion.div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab 3: Profile
// ---------------------------------------------------------------------------

interface ProfileTabProps {
  userId: string
  userEmail: string | undefined
  initialProfile: {
    full_name: string | null
    school_id: string | null
    sport: string | null
    gender: string | null
    graduation_year: number | null
    avatar_url: string | null
  }
  onSignOut: () => void
}

function ProfileTab({ userId, userEmail, initialProfile, onSignOut }: ProfileTabProps) {
  const [formData, setFormData] = useState({
    full_name: initialProfile.full_name ?? '',
    sport: initialProfile.sport ?? '',
    gender: initialProfile.gender ?? '',
    graduation_year: initialProfile.graduation_year ?? new Date().getFullYear(),
  })
  const [schoolName, setSchoolName] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Avatar state
  const [avatarUrl, setAvatarUrl] = useState<string | null>(initialProfile.avatar_url)
  const [avatarFile, setAvatarFile] = useState<File | null>(null)
  const [avatarPreview, setAvatarPreview] = useState<string | null>(initialProfile.avatar_url)
  const [uploadingAvatar, setUploadingAvatar] = useState(false)
  const [avatarError, setAvatarError] = useState<string | null>(null)
  const avatarInputRef = React.useRef<HTMLInputElement>(null)

  // Password reset state
  const [sendingReset, setSendingReset] = useState(false)
  const [resetMsg, setResetMsg] = useState<string | null>(null)
  const [resetError, setResetError] = useState<string | null>(null)

  // Fetch school name from school_id
  useEffect(() => {
    if (!initialProfile.school_id) return
    const fetchSchool = async () => {
      const { data } = await supabase
        .from('schools')
        .select('institution_name')
        .eq('school_id', parseInt(initialProfile.school_id as string, 10))
        .maybeSingle()
      if (data) {
        setSchoolName((data as { institution_name: string }).institution_name)
      }
    }
    fetchSchool()
  }, [initialProfile.school_id])

  // Handle avatar file selection — generate a preview
  function handleAvatarChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    setAvatarFile(file)
    setAvatarError(null)
    const reader = new FileReader()
    reader.onload = (ev) => {
      setAvatarPreview(ev.target?.result as string)
    }
    reader.readAsDataURL(file)
  }

  // Upload avatar to Supabase Storage and update profile
  async function handleAvatarUpload() {
    if (!avatarFile) return
    setUploadingAvatar(true)
    setAvatarError(null)

    try {
      const ext = avatarFile.name.split('.').pop() ?? 'jpg'
      const filePath = `${userId}/avatar.${ext}`

      // Attempt upload — gracefully handle if storage bucket doesn't exist
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, avatarFile, { upsert: true })

      if (uploadError) {
        // If bucket not found or storage error, skip gracefully
        if (
          uploadError.message.toLowerCase().includes('bucket') ||
          uploadError.message.toLowerCase().includes('not found') ||
          uploadError.message.toLowerCase().includes('storage')
        ) {
          setAvatarError('Avatar storage is not available yet. Profile info will still save.')
          setUploadingAvatar(false)
          return
        }
        throw new Error(uploadError.message)
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath)

      const publicUrl = urlData.publicUrl

      // Update profile with avatar_url
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', userId)

      if (profileError) throw new Error(profileError.message)

      setAvatarUrl(publicUrl)
      setAvatarFile(null)
      setSaveMsg('Avatar updated!')
      setTimeout(() => setSaveMsg(null), 2500)
    } catch (err: unknown) {
      setAvatarError(
        err instanceof Error ? err.message : 'Failed to upload avatar.'
      )
    } finally {
      setUploadingAvatar(false)
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    setSaveError(null)
    setSaveMsg(null)

    const { error } = await supabase
      .from('profiles')
      .update({
        full_name: formData.full_name || null,
        sport: formData.sport || null,
        gender: formData.gender || null,
        graduation_year: formData.graduation_year || null,
      })
      .eq('id', userId)

    setSaving(false)
    if (error) {
      setSaveError(error.message)
    } else {
      setSaveMsg('Saved!')
      setTimeout(() => setSaveMsg(null), 2500)
    }
  }

  async function handleSendPasswordReset() {
    if (!userEmail) return
    setSendingReset(true)
    setResetError(null)
    setResetMsg(null)

    const { error } = await supabase.auth.resetPasswordForEmail(userEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    })

    setSendingReset(false)
    if (error) {
      setResetError(error.message)
    } else {
      setResetMsg('Password reset email sent! Check your inbox.')
      setTimeout(() => setResetMsg(null), 5000)
    }
  }

  // Derived avatar initial for placeholder
  const avatarInitial =
    formData.full_name?.charAt(0)?.toUpperCase() ??
    userEmail?.charAt(0)?.toUpperCase() ??
    '?'

  const genderOptions = ["Men's", "Women's", "Mixed", "Co-ed"]

  return (
    <div className="space-y-6">

      {/* ── Avatar Upload ─────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#14151F] border border-[#2a2a3c] rounded-2xl p-6"
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-[#8888a8] mb-4">
          Profile Photo
        </p>

        <div className="flex items-center gap-5">
          {/* Avatar preview */}
          <div className="shrink-0">
            {avatarPreview ? (
              <img
                src={avatarPreview}
                alt="Avatar preview"
                className="w-20 h-20 rounded-full object-cover border-2 border-[rgba(20,184,166,0.40)]"
              />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[rgba(20,184,166,0.20)] border-2 border-[rgba(20,184,166,0.40)] flex items-center justify-center">
                <span className="text-[#14B8A6] font-bold text-2xl">{avatarInitial}</span>
              </div>
            )}
          </div>

          {/* Upload controls */}
          <div className="flex-1 space-y-3">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => avatarInputRef.current?.click()}
                className="flex items-center gap-2 border border-[#3a3a52] text-[#a8a8c0] hover:border-[#14B8A6]/60 hover:text-[#14B8A6] px-4 py-2 rounded-xl text-xs font-medium transition-colors"
              >
                <Upload size={13} />
                Choose Photo
              </button>
              {avatarFile && (
                <button
                  type="button"
                  onClick={handleAvatarUpload}
                  disabled={uploadingAvatar}
                  className="flex items-center gap-2 bg-[#14B8A6] hover:bg-[#14B8A6] disabled:bg-[rgba(20,184,166,0.40)] text-black font-bold px-4 py-2 rounded-xl text-xs transition-colors"
                >
                  {uploadingAvatar ? (
                    <>
                      <div className="w-3 h-3 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Uploading…
                    </>
                  ) : (
                    <>
                      <CheckCircle size={13} />
                      Save Photo
                    </>
                  )}
                </button>
              )}
              {avatarFile && (
                <button
                  type="button"
                  onClick={() => {
                    setAvatarFile(null)
                    setAvatarPreview(avatarUrl)
                    setAvatarError(null)
                    if (avatarInputRef.current) avatarInputRef.current.value = ''
                  }}
                  className="text-[#555570] hover:text-[#a8a8c0] text-xs px-2 py-2 transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarChange}
              className="hidden"
            />
            <p className="text-[#555570] text-xs">
              JPG, PNG, or GIF · Max 5 MB
            </p>
            {avatarError && (
              <p className="text-red-400 text-xs flex items-center gap-1.5">
                <AlertCircle size={12} />
                {avatarError}
              </p>
            )}
          </div>
        </div>
      </motion.div>

      {/* ── Profile Edit Form ─────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-[#14151F] border border-[#2a2a3c] rounded-2xl p-6"
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-[#8888a8] mb-5 flex items-center gap-2">
          <Edit2 size={13} className="text-[#14B8A6]" />
          Edit Profile
        </p>

        <form onSubmit={handleSave} className="space-y-4">
          {/* Full Name */}
          <div>
            <label className="block text-[#a8a8c0] text-sm font-medium mb-1.5">
              Full Name
            </label>
            <input
              type="text"
              value={formData.full_name}
              onChange={(e) => setFormData((p) => ({ ...p, full_name: e.target.value }))}
              placeholder="Your full name"
              className="w-full bg-[#1E1F2E] border border-[#2a2a3c] rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[rgba(20,184,166,0.50)] transition-colors"
            />
          </div>

          {/* Email — read only */}
          <div>
            <label className="block text-[#a8a8c0] text-sm font-medium mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={userEmail ?? ''}
              readOnly
              className="w-full bg-[rgba(37,37,64,0.50)] border border-[#2a2a3c]/50 rounded-xl px-4 py-3 text-[#8888a8] text-sm cursor-not-allowed"
            />
          </div>

          {/* School — read only */}
          <div>
            <label className="block text-[#a8a8c0] text-sm font-medium mb-1.5">
              School
            </label>
            <input
              type="text"
              value={schoolName ?? (initialProfile.school_id ? 'Loading…' : 'Not set')}
              readOnly
              className="w-full bg-[rgba(37,37,64,0.50)] border border-[#2a2a3c]/50 rounded-xl px-4 py-3 text-[#8888a8] text-sm cursor-not-allowed"
            />
            <p className="text-[#555570] text-xs mt-1">
              Contact support to update your school affiliation.
            </p>
          </div>

          {/* Sport */}
          <div>
            <label className="block text-[#a8a8c0] text-sm font-medium mb-1.5">
              Sport
            </label>
            <input
              type="text"
              value={formData.sport}
              onChange={(e) => setFormData((p) => ({ ...p, sport: e.target.value }))}
              placeholder="e.g. Basketball, Soccer, Swimming"
              className="w-full bg-[#1E1F2E] border border-[#2a2a3c] rounded-xl px-4 py-3 text-white text-sm placeholder-zinc-600 focus:outline-none focus:border-[rgba(20,184,166,0.50)] transition-colors"
            />
          </div>

          {/* Gender Division */}
          <div>
            <label className="block text-[#a8a8c0] text-sm font-medium mb-1.5">
              Gender Division
            </label>
            <select
              value={formData.gender}
              onChange={(e) => setFormData((p) => ({ ...p, gender: e.target.value }))}
              className="w-full bg-[#1E1F2E] border border-[#2a2a3c] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[rgba(20,184,166,0.50)] transition-colors"
            >
              <option value="">Select division</option>
              {genderOptions.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>

          {/* Graduation Year */}
          <div>
            <label className="block text-[#a8a8c0] text-sm font-medium mb-1.5">
              Graduation Year
            </label>
            <input
              type="number"
              min={2000}
              max={2035}
              value={formData.graduation_year}
              onChange={(e) =>
                setFormData((p) => ({ ...p, graduation_year: parseInt(e.target.value, 10) }))
              }
              className="w-full bg-[#1E1F2E] border border-[#2a2a3c] rounded-xl px-4 py-3 text-white text-sm focus:outline-none focus:border-[rgba(20,184,166,0.50)] transition-colors"
            />
          </div>

          {/* Error */}
          {saveError && (
            <div className="flex items-center gap-2 text-red-400 text-xs">
              <AlertCircle size={14} />
              {saveError}
            </div>
          )}

          {/* Save button */}
          <div className="flex items-center gap-4 pt-1">
            <button
              type="submit"
              disabled={saving}
              className="bg-[#14B8A6] text-black font-bold px-6 py-2.5 rounded-xl hover:bg-[#14B8A6] transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  Saving…
                </>
              ) : (
                'Save Changes'
              )}
            </button>

            {/* Inline success toast */}
            <AnimatePresence>
              {saveMsg && (
                <motion.span
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5 text-green-400 text-sm font-medium"
                >
                  <CheckCircle size={16} />
                  {saveMsg}
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </form>
      </motion.div>

      {/* ── Change Password ───────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-[#14151F] border border-[#2a2a3c] rounded-2xl p-6"
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-[#8888a8] mb-4">
          Security
        </p>
        <p className="text-[#a8a8c0] text-sm mb-4">
          To change your password, we'll send a reset link to{' '}
          <span className="text-[#14B8A6]">{userEmail}</span>.
        </p>

        <div className="flex flex-wrap items-center gap-4">
          <button
            type="button"
            onClick={handleSendPasswordReset}
            disabled={sendingReset || !userEmail}
            className="flex items-center gap-2 border border-[#3a3a52] text-[#a8a8c0] hover:border-[#14B8A6]/60 hover:text-[#14B8A6] px-4 py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sendingReset ? (
              <>
                <div className="w-4 h-4 border-2 border-[#8888a8] border-t-transparent rounded-full animate-spin" />
                Sending…
              </>
            ) : (
              'Send Password Reset Email'
            )}
          </button>

          <AnimatePresence>
            {resetMsg && (
              <motion.span
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-1.5 text-green-400 text-sm font-medium"
              >
                <CheckCircle size={16} />
                {resetMsg}
              </motion.span>
            )}
            {resetError && (
              <motion.span
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-1.5 text-red-400 text-sm"
              >
                <AlertCircle size={14} />
                {resetError}
              </motion.span>
            )}
          </AnimatePresence>
        </div>
      </motion.div>

      {/* ── Sign Out ──────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="bg-[#14151F] border border-[#2a2a3c] rounded-2xl p-6"
      >
        <p className="text-xs font-semibold uppercase tracking-widest text-[#8888a8] mb-4">
          Account Actions
        </p>
        <button
          onClick={onSignOut}
          className="flex items-center gap-2 border border-[#3a3a52] text-[#a8a8c0] hover:border-red-500/60 hover:text-red-400 px-4 py-2.5 rounded-xl text-sm transition-colors"
        >
          <LogOut size={16} />
          Sign Out
        </button>
      </motion.div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// UserDashboard — Main
// ---------------------------------------------------------------------------

export function UserDashboard() {
  const { user, profile, loading } = useAuth()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<Tab>('reviews')

  const tabs: { id: Tab; label: string; icon: React.ElementType }[] = [
    { id: 'reviews', label: 'My Reviews', icon: FileText },
    { id: 'verification', label: 'Verification', icon: Shield },
    { id: 'profile', label: 'Profile', icon: User },
  ]

  async function handleSignOut() {
    await supabase.auth.signOut()
    navigate('/')
  }

  // Avatar initial
  const avatarInitial =
    profile?.full_name?.charAt(0)?.toUpperCase() ??
    user?.email?.charAt(0)?.toUpperCase() ??
    '?'

  const displayName = profile?.full_name ?? user?.email ?? 'Athlete'

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E1A] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#14B8A6] border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  if (!user) {
    navigate('/login')
    return null
  }

  return (
    <div className="min-h-screen bg-[#0A0E1A] text-white pb-24 sm:pb-8">
      {/* Page header */}
      <div className="border-b border-[#2a2a3c] bg-[rgba(20,21,31,0.60)] backdrop-blur-sm">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 sm:pt-20 pb-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center gap-4"
          >
            {/* Avatar */}
            {profile?.avatar_url ? (
              <img
                src={profile.avatar_url}
                alt={displayName}
                className="w-14 h-14 rounded-full object-cover border-2 border-[rgba(20,184,166,0.40)]"
              />
            ) : (
              <div className="w-14 h-14 rounded-full bg-[rgba(20,184,166,0.20)] border-2 border-[rgba(20,184,166,0.40)] flex items-center justify-center shrink-0">
                <span className="text-[#14B8A6] font-bold text-xl">{avatarInitial}</span>
              </div>
            )}

            {/* Name + status */}
            <div>
              <h1 className="text-2xl font-bold text-white">My Dashboard</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="text-[#8888a8] text-sm">{displayName}</p>
                {profile?.verification_status === 'approved' && (
                  <span className="inline-flex items-center gap-1 bg-green-500/20 text-green-400 text-xs font-medium px-2 py-0.5 rounded-full border border-green-500/30">
                    <CheckCircle size={11} /> Verified
                  </span>
                )}
                {profile?.verification_status === 'pending' && (
                  <span className="inline-flex items-center gap-1 bg-[rgba(20,184,166,0.20)] text-[#14B8A6] text-xs font-medium px-2 py-0.5 rounded-full border border-[rgba(20,184,166,0.30)]">
                    <Clock size={11} /> Pending Verification
                  </span>
                )}
              </div>
            </div>
          </motion.div>

          {/* Tab bar */}
          <div className="mt-6 flex gap-1 border-b border-[#2a2a3c] -mb-px">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const isActive = activeTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`relative flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium transition-colors ${
                    isActive ? 'text-[#14B8A6]' : 'text-[#8888a8] hover:text-[#c4c4dc]'
                  }`}
                >
                  <Icon size={15} />
                  {tab.label}
                  {isActive && (
                    <motion.div
                      layoutId="tab-indicator"
                      className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#14B8A6] rounded-full"
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Tab content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8">
        <AnimatePresence mode="wait">
          {activeTab === 'reviews' && (
            <motion.div
              key="reviews"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <ReviewsTab userId={user.id} />
            </motion.div>
          )}

          {activeTab === 'verification' && (
            <motion.div
              key="verification"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <VerificationTab
                userId={user.id}
                schoolId={profile?.school_id ?? null}
                sport={profile?.sport ?? null}
                gender={profile?.gender ?? null}
                graduationYear={profile?.graduation_year ?? null}
                verificationStatus={profile?.verification_status ?? 'pending'}
              />
            </motion.div>
          )}

          {activeTab === 'profile' && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
            >
              <ProfileTab
                userId={user.id}
                userEmail={user.email}
                initialProfile={{
                  full_name: profile?.full_name ?? null,
                  school_id: profile?.school_id ?? null,
                  sport: profile?.sport ?? null,
                  gender: profile?.gender ?? null,
                  graduation_year: profile?.graduation_year ?? null,
                  avatar_url: profile?.avatar_url ?? null,
                }}
                onSignOut={handleSignOut}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default UserDashboard
